import type { SlaItem, SlaState } from '@rp/contracts'
import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { isApiError } from '../../api/client'
import { Chip, LedgerRow, Pager, Spinner } from '../../design'
import { formatDateTime } from '../../lib/time'
import { pageProblemOf } from '../queue/labels'
import { OPERATIONS_COPY } from './copy'
import { SLA_PAGE_LIMIT, useSlaItems } from './queries'

const LIST_COPY: Record<SlaState, { title: string; empty: string; chip: string }> = {
  at_risk: {
    title: OPERATIONS_COPY.slaAtRiskList,
    empty: OPERATIONS_COPY.slaAtRiskEmpty,
    chip: OPERATIONS_COPY.slaAtRisk,
  },
  breached: {
    title: OPERATIONS_COPY.slaBreachedList,
    empty: OPERATIONS_COPY.slaBreachedEmpty,
    chip: OPERATIONS_COPY.slaBreached,
  },
}

const SlaRow = ({ item }: { item: SlaItem }) => {
  const pageProblem = pageProblemOf(item.pageConnection)
  const breached = item.state === 'breached'
  return (
    <LedgerRow>
      <div className="ops-row">
        <span className="ops-row-title">{item.authorName}</span>
        <Chip tone={breached ? 'danger' : 'warning'} variant={breached ? 'solid' : 'wash'}>
          {LIST_COPY[item.state].chip}
        </Chip>
        {pageProblem === null ? null : (
          <Chip tone="danger" variant="outline">
            {pageProblem}
          </Chip>
        )}
        <span className="ops-row-time num">{formatDateTime(new Date(item.replyDeadlineAt))}</span>
        <Link to="/items/$itemId" params={{ itemId: item.conversationId }} className="ops-row-link">
          {OPERATIONS_COPY.openItem}
        </Link>
      </div>
    </LedgerRow>
  )
}

const LoadFailure = ({ error }: { error: unknown }) => (
  <p className="operations-error tone-danger" role="alert">
    {isApiError(error) ? error.message : OPERATIONS_COPY.slaLoadFailed}
  </p>
)

const pagesOf = (total: number): number => Math.max(1, Math.ceil(total / SLA_PAGE_LIMIT))

export const SlaList = ({ state }: { state: SlaState }) => {
  const [page, setPage] = useState(1)
  const query = useSlaItems(state, page)
  const copy = LIST_COPY[state]
  const lastPage = query.data === undefined ? null : pagesOf(query.data.total)

  useEffect(() => {
    if (lastPage !== null && page > lastPage) setPage(lastPage)
  }, [lastPage, page])

  if (lastPage !== null && page > lastPage) return <Spinner label={OPERATIONS_COPY.slaLoading} />
  if (query.data === undefined) {
    return query.isPending ? <Spinner label={OPERATIONS_COPY.slaLoading} /> : <LoadFailure error={query.error} />
  }

  const { items, total } = query.data
  const pages = pagesOf(total)
  return (
    <div className="ops-subsection">
      <h3 className="ops-subsection-title">{copy.title}</h3>
      {items.length === 0 ? (
        <p className="ops-empty">{copy.empty}</p>
      ) : (
        <ul className="ledger-list ops-list" aria-label={copy.title}>
          {items.map((item) => (
            <SlaRow key={item.conversationId} item={item} />
          ))}
        </ul>
      )}
      {pages <= 1 ? null : (
        <Pager
          label={OPERATIONS_COPY.slaPager(copy.title)}
          status={OPERATIONS_COPY.pageStatus(page, pages, total)}
          onPrevious={() => setPage(page - 1)}
          onNext={() => setPage(page + 1)}
          previousDisabled={page <= 1}
          nextDisabled={page >= pages}
        />
      )}
    </div>
  )
}
